import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NbComponentStatus, NbPopoverDirective, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, Subscription, combineLatest, interval } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeWhile } from 'rxjs/operators';

import { TailorService } from '../tailor.service';
import { CvService } from '../cv.service';
import { HouseExemplarService } from '../house-exemplars/house-exemplar.service';
import { MarketVacancyService } from '../../shared/market-vacancy.service';
import { AuthService } from '../../shared/auth.service';
import { DirtyDeactivateGuard } from '../../shared/dirty-deactivate-guard.service';
import { SubmissionService } from '../../shared/submission.service';
import { CvTemplate } from '../../interfaces/cv';
import { MarketVacancy } from '../../interfaces/market-vacancy';
import { Submission } from '../../interfaces/submission';
import {
  ApplicationPackage,
  ArtifactKind,
  ClaimStrength,
  ConversationTurn,
  CoverageReport,
  CoverageRequirement,
  CreatePackageRequest,
  CrmSyncState,
  DocDiff,
  EducationInclusion,
  ExperienceInclusion,
  HumanEditOp,
  HumanEditRequestBody,
  LetterDocumentView,
  PackageShowResponse,
  ProposalRef,
  RejectedOp,
  SectionInclusionItem,
  SectionKey,
  TailorTurnResponse,
  TailoringQuota
} from '../../interfaces/application-package';

/**
 * A single manually-editable field, chosen via the workspace's Edit buttons.
 * Drives the shared bst-field-editor pane; Save posts the matching
 * HumanEditOp (or a replaced letter paragraph or signature) through TailorService.edit().
 */
type EditSelection =
  | { kind: 'cv'; field: 'position' | 'profile_text' }
  | { kind: 'cv'; field: 'role' | 'description'; experienceId: number; label: string; current: string }
  | { kind: 'cv'; field: 'education_description'; educationId: number; label: string; current: string }
  | { kind: 'cv'; field: 'skill_label'; skillId: number; label: string; current: string }
  | { kind: 'cv'; field: 'soft_skill_label'; skillId: number; label: string; current: string }
  | { kind: 'letter'; field: 'paragraph'; index: number; label: string; current: string }
  | { kind: 'letter'; field: 'signature'; label: string; current: string }
  | { kind: 'cv'; field: 'contact'; contactField: 'email' | 'phone' | 'linkedin_url' | 'address' }
  | { kind: 'cv'; field: 'period'; section: 'experience' | 'education'; id: number; label: string; start: string | null; end: string | null }
  | { kind: 'cv'; field: 'education_text'; educationId: number; edField: 'degree' | 'role' | 'employer'; label: string; current: string };

/**
 * Tailor workspace: the main surface of a tailoring session. The left pane
 * shows the live reframed CV preview, a readable change view and a hard
 * requirement coverage scoreboard; the right pane hosts the refine chat. The
 * approve bar finalizes the package to CV + letter PDFs.
 *
 * This component reads, orchestrates and finalizes; it never computes a new
 * document itself. There are three mutation paths, all server-side and all
 * folding their response through onTurn()/applyPartialTurn(), which carry a
 * fresh diff + coverage and force the stored-document preview to refetch (no
 * LLM call in the preview itself): the refine chat, the inclusion cockpit's
 * set-aside/restore endpoints, and — behind CV_MANUAL_EDIT_ENABLED —
 * saveEdit() plus the reorder/drag handlers, which post HumanEditOps to
 * tailor.edit().
 */
// Ceiling for a measured preview's content height, in the same px unit as
// scrollHeight and --{cv,letter}-content-height. Bounds the stage's CSS
// height (contentHeight * scale) against a bad measurement or a scale that
// fails to compute as a 0-1 fraction: either turns a modest document into an
// unbounded scroll, and neither can be ruled out from this file alone. Far
// above any real CV or letter (roughly 35 A4 pages at 794x1123).
const MAX_PREVIEW_CONTENT_HEIGHT_PX = 40000;

@Component({
  selector: 'bst-cv-tailor',
  templateUrl: './cv-tailor.component.html',
  styleUrls: ['./cv-tailor.component.scss'],
  standalone: false
})
export class CvTailorComponent implements OnInit, OnDestroy {
  // Exposed for the template: bound as both stages' max-height so the CSS
  // ceiling can never drift from the one the JS clamp below actually uses.
  public readonly maxPreviewContentHeightPx = MAX_PREVIEW_CONTENT_HEIGHT_PX;


  public pkg: ApplicationPackage | null = null;
  public diff: DocDiff = {};
  public coverage: CoverageReport = { matched: 0, total: 0, missing: [] };
  public rejectedOps: RejectedOp[] = [];
  /** Consultant's personal monthly AI-turn quota; null for privileged callers or an unlimited cap. */
  public quota: TailoringQuota | null = null;
  /** Prior chat turns, passed down to seed the refine chat on (re)load. */
  public conversation: ConversationTurn[] = [];
  // Per-kind splits of `conversation`, one per kept-alive chat instance: each
  // chat is single-kind, so it must only see its own history and its own
  // round counter (a past refine adds one; consults do not). Derived from
  // `conversation` via rebuildKindConversations().
  public cvConversation: ConversationTurn[] = [];
  public letterConversation: ConversationTurn[] = [];
  public cvTurnCount = 1;
  public letterTurnCount = 1;
  /** Each artifact's still-open edit-review proposal (if any), keyed by kind. */
  public pendingProposal: { cv: ProposalRef | null; letter: ProposalRef | null } | null = null;
  public previewHtml: SafeHtml | null = null;

  // Review state is DERIVED from pendingProposal, never mirrored into fields of
  // its own: accept/reject clear pendingProposal, and anything held separately
  // would have to be cleared on each of those paths too, which is exactly the
  // kind of second clear that gets forgotten and leaves a resolved proposal on
  // screen.

  /** Whether the CV has a still-open proposal (this is what opens its view). */
  public get hasCvProposal(): boolean {
    return !!this.pendingProposal?.cv;
  }

  /** A pending CV proposal's before->after, or null when none is open. */
  public get proposedDiff(): DocDiff | null {
    return this.pendingProposal?.cv?.diff ?? null;
  }

  /** A pending letter proposal's proposed document, or null when none is open. */
  public get proposedLetter(): LetterDocumentView | null {
    return this.pendingProposal?.letter?.letter ?? null;
  }

  /**
   * Which diff the rows render, chosen by the view the user is ON, never by
   * substitution. "What would this proposal change?" and "what has changed so
   * far?" are different questions, so they are separate views and both stay
   * reachable while a proposal is open. Preferring the proposal inside one
   * shared view made the second question unanswerable, and silently handed
   * every consumer of the cumulative diff a value none of them was written for.
   */
  public get reviewDiff(): DocDiff {
    return this.cvView === 'proposal' ? (this.proposedDiff ?? {}) : this.diff;
  }

  /**
   * Single write path for the pending map. The proposal view only exists while
   * a CV proposal does, so every path that resolves, supersedes or replaces one
   * has to be able to send the view back; routing them all through here is what
   * keeps that from being a separate clear somebody forgets on path number six.
   */
  private setPendingProposal(p: { cv: ProposalRef | null; letter: ProposalRef | null } | null): void {
    this.pendingProposal = p;
    if (this.cvView === 'proposal' && !p?.cv) { this.cvView = 'changes'; }
  }

  public previewLoading = false;
  /**
   * Measured height (px, native/unscaled) of the loaded preview iframe's
   * content, read off the iframe once its srcdoc finishes loading. The CV
   * template renders N .cv-page sections stacked in document flow (one per
   * physical page, see cv/layout.blade.php), so this is N * ~1123px for an
   * N-page CV, not always one page. null (unmeasured) falls back to a single
   * page's height in the template.
   */
  public previewContentHeight: number | null = null;
  /** Set when a preview fetch fails and no prior HTML exists to fall back on,
      so the pane shows a retry state instead of staying blank once the error
      toast (the only other signal) has faded. Cleared at the start of every
      refreshPreview() attempt. */
  public previewFailed = false;
  /** Letter counterpart of previewHtml/previewLoading/previewFailed/
      previewContentHeight: the stored letter rendered in the tenant's letter
      template (GET .../preview/letter), shown in its own iframe on the letter
      tab. Refreshed from refreshPreview() and from every poll tick (the letter
      can land before the CV); re-fetched only when the stored letter itself
      changed (see refreshLetterPreview). */
  public letterPreviewHtml: SafeHtml | null = null;
  public letterPreviewLoading = false;
  public letterPreviewFailed = false;
  public letterPreviewContentHeight: number | null = null;
  private letterPreviewFingerprint: string | null = null;
  /** Monotonic id of the latest letter fetch; a response from an older fetch
      is dropped so an out-of-order reply cannot leave a stale letter on
      screen behind a fingerprint that says it is current. */
  private letterPreviewSeq = 0;
  public loading = false;
  public loadFailed = false;
  public finalizing = false;
  /** Set while a promote-to-house-exemplar request is in flight. */
  public promoting = false;

  /** Which artifact tab is active; drives the refine chat's target kind. */
  public activeTab: ArtifactKind = 'cv';
  public cvView: 'preview' | 'changes' | 'proposal' | 'experiences' = 'preview';
  /** Letter tab: the templated iframe, or the paragraph card with the manual-edit controls (offered only while manual editing is on). */
  public letterView: 'preview' | 'edit' = 'preview';
  public inclusionBusy = false;
  /** Mirrors each kept-alive chat instance's own `sending` (via its busyChange
      output), kept separate per kind: a single shared flag let one chat's turn
      finishing clear the OTHER's still-in-flight loading state (e.g. a letter
      refine completing dropped the CV coverage board's loader mid-CV-refine). */
  public cvChatBusy = false;
  public letterChatBusy = false;
  public drawerOpen = false;

  /** A refine/inclusion turn is in flight, so the coverage board would otherwise
      show stale numbers until the response lands, so this overlays a loader.
      Reflects the ACTIVE tab's chat only, since this drives the CV coverage board. */
  public get turnInFlight(): boolean {
    return this.inclusionBusy
      || (this.activeTab === 'cv' ? (this.cvChatBusy || this.previewLoading) : this.letterChatBusy);
  }

  // Create-mode: when this surface loads on /cv-generator/tailor/new (no
  // :packageId), it absorbs the intake. It fetches CV defaults, then either
  // auto-creates the package (a vacancy is carried) or shows the picker first.
  public mode: 'load' | 'create' = 'load';
  public creating = false;
  public createFailed = false;
  public pickerVisible = false;
  public templates: CvTemplate[] = [];

  // Create-mode landing: a live-debounced vacancy search + a paste-a-vacancy
  // path, shown when no vacancy was carried in the intake.
  public landingQuery = new FormControl<string>('');
  public landingResults: MarketVacancy[] = [];
  /** CRM deals (submissions) of the subject consultant matching the query. */
  public landingDeals: Submission[] = [];

  /** Deals lookup is staffer/admin + CRM-flag only (mirrors SubmissionPolicy::viewAny). */
  get canSearchDeals(): boolean {
    return (this.auth.isAdmin || this.auth.isStaffer) && !!this.auth.user.getValue()?.crm_enabled;
  }
  public landingSearching = false;
  public landingError = false;
  public landingPasteOpen = false;
  public pastedText = '';
  public landingPaste = false;
  private landingWired = false;
  private cSubject!: number;
  private cVacancy: number | null = null;
  private cArtifacts: ArtifactKind[] = ['cv'];
  private cClientId?: number;
  private cTemplateType = 'built_in';
  private cClientCvTemplateId: number | null = null;
  private cTemplateId = 'editorial';
  private cLocale = 'nl';
  /** CRM: submission to auto-link after package create (#767). Null when not in a CRM flow. */
  private cSubmissionId: number | null = null;

  private packageId!: number;
  private subs: Subscription[] = [];
  /** Active generation poll (one at a time); torn down on completion or re-entry. */
  private pollSub?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly tailor: TailorService,
    private readonly cv: CvService,
    private readonly vacancies: MarketVacancyService,
    private readonly submissions: SubmissionService,
    private readonly toastr: NbToastrService,
    private readonly translate: TranslateService,
    private readonly sanitizer: DomSanitizer,
    private readonly houseExemplars: HouseExemplarService,
    private readonly auth: AuthService,
    private readonly dirtyGuard: DirtyDeactivateGuard
  ) {}

  // Release (finalize + download) is privileged. The consultant refines and
  // submits; management releases. Server-side policy is the real gate, this is
  // display only.
  //
  // Read the server's answer rather than the caller's role: release() also
  // requires a staffer's department scope to cover the subject, so role alone
  // showed a staffer Approve and Download on their OWN package (they are not
  // their own direct report) and the server refused both. Download is a plain
  // <a target="_blank">, so that refusal landed as raw JSON in a new tab. The
  // role flags stay as the fallback for a payload that predates can_release,
  // which keeps the pre-existing behaviour rather than blanking the bar.
  public get canRelease(): boolean {
    return this.pkg?.can_release ?? (this.auth.isAdmin || this.auth.isStaffer || this.auth.isSuperuser);
  }

  // The consultant half of the same contract: server-decided, so a caller who
  // cannot submit is not offered the button. Absent means the server did not
  // say, and the server is the real gate, so default to showing it rather than
  // hiding the only action a consultant has.
  public get canSubmit(): boolean {
    return this.pkg?.can_submit ?? true;
  }

  // Consultant edit window: closed once submitted or finalized. A package may
  // contain a CV, a letter, or both, so either artifact reaching 'finalized'
  // must lock the workspace (a letter-only package never sets cv_status).
  public get isLocked(): boolean {
    if (this.canRelease) { return false; }
    return !!this.pkg?.submitted_at || this.cvFinalized || this.letterFinalized;
  }

  public submitting = false;

  public submit(): void {
    if (!this.pkg || this.submitting) { return; }
    this.submitting = true;
    this.tailor.submit(this.pkg.id).subscribe({
      next: p => { this.pkg = p; this.submitting = false; },
      error: err => {
        this.submitting = false;
        this.toastr.danger(
          err?.error?.error === 'already_submitted'
            ? this.translate.instant('cv.tailor.submit.alreadySubmitted')
            : this.translate.instant('cv.tailor.submit.failed'),
          this.translate.instant('error'),
        );
      },
    });
  }

  /**
   * Return from the tailor workspace to where the caller can browse their
   * documents. A CRM-launched generation (cSubmissionId set) returns to that
   * submission — otherwise the generated doc is orphaned from the deal that
   * asked for it, the exact "doesn't wire back" gap reported live. Privileged
   * callers with no CRM context go to the CV generator hub; a role-1
   * consultant cannot enter that route (AuthGuard restricts it to
   * ADMIN/STAFFER) and reached this workspace via the market vacancy
   * launcher, not the hub, so send them to their own profile instead.
   */
  backToList(): void {
    if (this.cSubmissionId) {
      this.router.navigate(['/submissions', this.cSubmissionId]);
      return;
    }
    this.router.navigate([this.auth.isUser ? '/users/me' : '/cv-generator']);
  }

  /** Human label for the package's target vacancy, shown in the workspace header. */
  get vacancyHeading(): string {
    if (this.pkg?.vacancy_title) {
      return this.pkg.vacancy_title;
    }
    if (this.pkg?.is_pasted_vacancy) {
      return this.translate.instant('cv.tailor.workspace.pastedVacancy');
    }
    return this.translate.instant('cv.tailor.workspace.untitledVacancy');
  }

  /**
   * CRM linkage outcome of the last handover (submit for review / approval).
   * Null until one has happened, or when the CRM is off for this tenant.
   */
  get crmSyncState(): CrmSyncState | null {
    return this.pkg?.crm_sync_state ?? null;
  }

  get crmSyncNote(): string | null {
    return this.pkg?.crm_sync_note ?? null;
  }

  // --------------------------------------------------------------------
  // Manual field editing: direct text edits, bypassing the AI chat. One
  // shared selection drives the bst-field-editor pane; Save posts a single
  // HumanEditOp (or a replaced letter paragraph) through tailor.edit() and
  // folds the show() envelope it returns through onTurn(), the same handler
  // the chat's accept/reject use, so the new Edit turn lands in the chat.
  // --------------------------------------------------------------------

  public selection: EditSelection | null = null;
  public selectionDirty = false;
  public editBusy = false;

  /**
   * The feature ships dark (CV_MANUAL_EDIT_ENABLED, default off): every
   * manual-edit affordance renders only when the server says the flag is on
   * for this package, and never inside a closed edit window. With the flag
   * off, POST /edits answers 404, so an ungated affordance is a dead button.
   */
  public get canManualEdit(): boolean {
    return !!this.pkg?.manual_edit_enabled && !this.isLocked;
  }

  /** Drives DirtyDeactivateGuard: an in-progress, unsaved edit blocks navigation. */
  public get isDirty(): boolean {
    return this.selectionDirty;
  }

  // Replacing the selection resets the editor draft, so a click on another
  // field mid-edit goes through the same unsaved-changes prompt as leaving
  // the page; re-clicking the open field keeps the draft. Only the dirty
  // case awaits; the plain path stays synchronous.
  public async selectField(sel: EditSelection): Promise<void> {
    if (!this.canManualEdit) { return; }
    if (this.selectionDirty && this.selection) {
      if (this.sameSelection(this.selection, sel)) { return; }
      if (!(await this.dirtyGuard.warnIfNeeded(this))) { return; }
    }
    this.selection = sel;
    this.selectionDirty = false;
  }

  /** Same target: identity keys only, never the label or current text. */
  private sameSelection(a: EditSelection, b: EditSelection): boolean {
    return a.kind === b.kind && a.field === b.field && this.selectionId(a) === this.selectionId(b);
  }

  private selectionId(sel: EditSelection): number | string | null {
    switch (sel.field) {
      case 'role': case 'description': return sel.experienceId;
      case 'education_description': return sel.educationId;
      case 'skill_label': case 'soft_skill_label': return sel.skillId;
      case 'contact': return sel.contactField;
      case 'paragraph': return sel.index;
      case 'signature': return 'signature';
      // section AND id: an experience id and an education id can collide numerically.
      case 'period': return `${sel.section}:${sel.id}`;
      // educationId AND edField: degree/role/employer share one educationId, so that
      // alone would collapse three distinct editable targets on the same row into one.
      case 'education_text': return `${sel.educationId}:${sel.edField}`;
      default: return null;
    }
  }

  public closeEditor(): void {
    this.selection = null;
    this.selectionDirty = false;
  }

  public saveEdit(text: string): void {
    if (!this.pkg || !this.selection) { return; }
    const sel = this.selection;
    if (sel.field === 'period') { return; }   // the period editor calls savePeriod()
    const body = sel.kind === 'letter'
      ? { kind: 'letter' as const, base_fingerprint: this.pkg.letter_fingerprint ?? null,
          paragraphs: sel.field === 'paragraph' ? this.letterParagraphsWith(sel.index, text) : this.letterParagraphs,
          signature: sel.field === 'signature' ? text : (this.pkg.letter_document?.signature ?? '') }
      : { kind: 'cv' as const, base_fingerprint: this.pkg.cv_fingerprint ?? null, ops: [this.opFor(sel, text)] };
    this.submitEdit(body);
  }

  public savePeriod(value: { start: string; end: string | null }): void {
    if (!this.pkg || !this.selection || this.selection.field !== 'period') { return; }
    this.submitEdit({ kind: 'cv' as const, base_fingerprint: this.pkg.cv_fingerprint ?? null, ops: [this.periodOp(value)] });
  }

  private submitEdit(body: HumanEditRequestBody): void {
    if (!this.pkg) { return; }
    this.editBusy = true;
    const sub = this.tailor.edit(this.pkg.id, body).subscribe({
      next: resp => {
        this.editBusy = false;
        this.closeEditor();
        this.onTurn(resp);
      },
      error: (err: HttpErrorResponse) => {
        this.editBusy = false;
        this.reportEditFailure(err);
      }
    });
    this.subs.push(sub);
  }

  /** True when the server refused a write because the document moved underneath the caller. */
  private isStaleDocument(err: HttpErrorResponse): boolean {
    return err.status === 409 && err.error?.error === 'stale_document';
  }

  /**
   * Message for a refused write. Every one of these endpoints answers
   * `{error: <reason>}` and never `{message}`, so the reason is looked up in
   * the shared cv.errors.inclusion.* table -- /edits and the direct-edit
   * endpoints raise the same PackageEditException reasons. A validation 422
   * (e.g. a reorder that splits an employer group) carries no reason at all,
   * only Laravel's own `message`, which is the next fallback.
   */
  private writeErrorMessage(err: HttpErrorResponse, fallbackKey: string): string {
    const code: string | undefined = err.error?.error;
    const key = code ? `cv.errors.inclusion.${code}` : null;
    const translated = key ? this.translate.instant(key) : null;
    if (translated && translated !== key) { return translated; }
    return err.error?.message || this.translate.instant(fallbackKey) || 'Your change could not be saved.';
  }

  /**
   * Single failure path for the manual-edit writes (field save + the three
   * drag/reorder paths). A stale document reloads the workspace, the way
   * applyInclusion() does. The drag paths additionally reload on ANY failure,
   * so a row the server refused to move snaps back to the stored order
   * instead of sitting where it was never saved.
   */
  private reportEditFailure(err: HttpErrorResponse, opts: { reload?: boolean } = {}): void {
    const title = this.translate.instant('cv.errors.title') || 'Error';
    if (this.isStaleDocument(err)) {
      this.toastr.warning(
        this.translate.instant('cv.tailor.edit.staleDocument') || 'Document changed elsewhere, reloaded.', title);
      this.load();
      return;
    }
    this.toastr.danger(this.writeErrorMessage(err, 'cv.errors.editFailed'), title);
    if (opts.reload) { this.load(); }
  }

  private opFor(sel: Exclude<EditSelection, { kind: 'letter' } | { field: 'period' }>, text: string) {
    switch (sel.field) {
      case 'position': return { op: 'set_role_line' as const, text };
      case 'profile_text': return { op: 'set_profile_text' as const, text };
      case 'role': return { op: 'set_experience_role_title' as const, experience_id: sel.experienceId, title: text };
      case 'description': return { op: 'reword_experience_bullet' as const, experience_id: sel.experienceId, text };
      case 'education_description': return { op: 'reword_education_description' as const, education_id: sel.educationId, text };
      case 'skill_label': return { op: 'set_skill_label' as const, skill_id: sel.skillId, label: text };
      case 'soft_skill_label': return { op: 'set_soft_skill_label' as const, skill_id: sel.skillId, label: text };
      case 'contact': return { op: 'set_header_contact' as const, field: sel.contactField, text };
      case 'education_text': return { op: 'set_education_text' as const, education_id: sel.educationId, field: sel.edField, text };
    }
  }

  private periodOp(value: { start: string; end: string | null }): HumanEditOp {
    const sel = this.selection;
    if (!sel || sel.field !== 'period') { throw new Error('periodOp without a period selection'); }
    return { op: 'set_period', section: sel.section, id: sel.id, start: value.start, end: value.end };
  }

  private letterParagraphsWith(index: number, text: string): string[] {
    const current = this.pkg?.letter_document?.paragraphs ?? [];
    const next = [...current];
    next[index] = text;
    return next;
  }

  // Template getters, not inline ternaries: EditSelection is a discriminated
  // union keyed on `field`, and only its first arm (position/profile_text)
  // lacks label/current. Chained ternaries inside a template binding put that
  // narrowing in Angular's own template type-checker, which `tsc --noEmit`
  // does not exercise and which narrows less reliably than plain TS control
  // flow -- so this logic lives here, in code `tsc` actually checks. Confirmed
  // by `tsc` while writing this: neither a plain `if` chain nor a combined
  // `if (sel.field === 'position' || sel.field === 'profile_text')` fully
  // narrows out that first arm ("Property 'label' does not exist on type
  // 'EditSelection'"), because its `field` is itself a two-value union, not a
  // single literal, and TS's discriminated-union narrowing is unreliable for
  // that shape across `if`/`===`/`||`. A `switch` on `field` (the same shape
  // `opFor()` below already relies on) narrows correctly.
  public get editorLabel(): string {
    const sel = this.selection;
    if (!sel) { return ''; }
    switch (sel.field) {
      case 'position': return this.translate.instant('cv.tailor.edit.fieldPosition');
      case 'profile_text': return this.translate.instant('cv.tailor.edit.fieldProfile');
      // Same reason as the two above, plus a hard one: Angular rejects a pipe
      // anywhere in an action expression, so the letter row's (click) cannot
      // translate its own label and passes ''.
      case 'paragraph': return this.translate.instant('cv.tailor.edit.fieldParagraph');
      case 'signature': return this.translate.instant('cv.tailor.edit.fieldSignature');
      case 'contact': return this.translate.instant(`cv.tailor.edit.field${sel.contactField === 'linkedin_url' ? 'Linkedin' : sel.contactField.charAt(0).toUpperCase() + sel.contactField.slice(1)}`);
      case 'period': return `${sel.label} · ${this.translate.instant('cv.tailor.edit.fieldPeriod')}`;
      case 'education_text': {
        const key = sel.edField === 'degree' ? 'fieldDegree' : sel.edField === 'role' ? 'fieldStudy' : 'fieldInstitution';
        return `${sel.label} · ${this.translate.instant('cv.tailor.edit.' + key)}`;
      }
      default: return sel.label;
    }
  }

  public get editorText(): string {
    const sel = this.selection;
    if (!sel) { return ''; }
    switch (sel.field) {
      case 'position': return this.pkg?.header?.position ?? '';
      case 'profile_text': return this.pkg?.header?.profile_text ?? '';
      case 'contact': return this.pkg?.header?.[sel.contactField] ?? '';
      case 'period': return '';   // unused: the period editor binds periodStart/periodEnd instead
      default: return sel.current;
    }
  }

  // One case per field, each returning that field's SERVER limit
  // (HumanEditRequest::rules() + validateOp()), because this is bound to the
  // textarea's native maxlength and the browser hard-blocks typing past it: a
  // value below the server's turns a valid edit into one the user cannot make
  // at all. Every cover-letter paragraph is longer than 200 characters.
  public get editorMaxLength(): number {
    const sel = this.selection;
    if (!sel) { return 200; }
    switch (sel.field) {
      case 'position': return 3000;               // set_role_line, max 3000
      case 'profile_text': return 3000;           // set_profile_text, max 3000
      case 'role': return 200;                    // set_experience_role_title, max 200
      case 'description': return 4000;            // reword_experience_bullet, max 4000
      case 'education_description': return 2000;  // reword_education_description, max 2000
      case 'skill_label': return 120;             // set_skill_label, max 120
      case 'soft_skill_label': return 120;        // set_soft_skill_label, max 120
      case 'paragraph': return 4000;              // paragraphs.*, max 4000
      case 'signature': return 255;               // signature, max 255
      case 'contact': return 255;                 // set_header_contact, max 255
      case 'education_text': return 200;          // set_education_text, max 200
      case 'period': return 0;                    // unused; keeps the switch exhaustive
    }
  }

  public get periodStart(): string | null { return this.selection?.field === 'period' ? this.selection.start : null; }
  public get periodEnd(): string | null { return this.selection?.field === 'period' ? this.selection.end : null; }

  ngOnInit(): void {
    // React to BOTH path and query params: packageId is a path param, but the
    // create inputs (subject/vacancy/artifacts/client) are query params, so a
    // 2nd navigation to /tailor/new with a different consultant changes only the
    // query. distinctUntilChanged on the relevant keys keeps exactly one entry
    // per distinct param set (no double create, no double load).
    this.subs.push(
      combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(
        map(([pm, qm]) => JSON.stringify({
          pid: pm.get('packageId'),
          subject: qm.get('subject'), vacancy: qm.get('vacancy'),
          artifacts: qm.get('artifacts'), client: qm.get('client'),
          submission: qm.get('submission'),
        })),
        distinctUntilChanged(),
      ).subscribe(() => {
        const pid = this.route.snapshot.paramMap.get('packageId');
        if (pid) { this.packageId = Number(pid); this.mode = 'load'; this.load(); return; }
        this.mode = 'create';
        this.enterCreateMode();
      })
    );
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
    if (this.previewClickDoc) {
      this.previewClickDoc.removeEventListener('click', this.previewClickHandler);
    }
  }

  // --------------------------------------------------------------------
  // Load
  // --------------------------------------------------------------------

  public load(): void {
    if (!this.packageId) {
      this.loadFailed = true;
      return;
    }
    this.loading = true;
    this.loadFailed = false;
    this.pollSub?.unsubscribe();
    // Re-derive from the URL, not just enterCreateMode(): the create ->
    // navigate hop lands here too (tailor/new and tailor/:packageId are
    // separate routes, so the component is recreated), and so does a plain
    // reload or a bookmarked link. Without this, cArtifacts silently resets
    // to its ['cv'] default and cvTabActive's gate below goes inert. Absent
    // an artifacts param (e.g. a cv-list row link, which carries no intent)
    // the ['cv'] default is the correct "no specific request" fallback.
    this.cArtifacts = this.parseArtifacts(this.route.snapshot.queryParamMap);

    const sub = this.tailor.getPackage(this.packageId).subscribe({
      next: resp => {
        this.applyFullTurn(resp);
        // Open on the artifact that actually has a draft or is generating
        // (letter-alone packages have no CV), so the active tab targets it.
        this.activeTab = this.cvTabActive ? 'cv' : 'letter';
        this.loading = false;
        // Populate templates once for the settings drawer's template select.
        if (!this.templates.length && resp.package.client_id) {
          this.subs.push(
            this.cv.getDefaults(resp.package.subject_user_id, { client_id: resp.package.client_id })
              .subscribe({ next: d => { this.templates = d.templates || []; }, error: () => {} })
          );
        }
        // A package opened mid-generation polls until each artifact lands.
        if (this.stillGenerating(resp.package)) { this.startPolling(); }
        this.refreshPreview();
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.loadFailed = true;
        console.error(err);
      }
    });
    this.subs.push(sub);
  }

  public refreshPreview(): void {
    if (!this.pkg) return;
    this.refreshLetterPreview();
    this.previewLoading = true;
    this.previewFailed = false;
    const sub = this.tailor.preview(this.pkg.id).subscribe({
      next: html => {
        // srcdoc is consumed by an isolated <iframe>; the server emits trusted
        // CV HTML. Do NOT inject this into the main document DOM.
        this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(html || '');
        // Reset now, not left over from the previous doc: this callback swaps
        // in the new iframe below without waiting for its own load event, so
        // an unreset value here would size the NEW iframe to the OLD CV's page
        // count until onPreviewIframeLoad re-measures a moment later.
        this.previewContentHeight = null;
        this.previewLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.previewLoading = false;
        // Only the blank-pane case (no prior HTML to fall back on) needs a
        // persistent retry state; a refresh failure with an existing preview
        // still on screen is fully covered by the toast below.
        if (!this.previewHtml) { this.previewFailed = true; }
        console.error(err);
        this.toastr.warning(
          this.previewErrorMessage(err, 'cv.errors.previewFailed'),
          this.translate.instant('cv.errors.title') || 'Error'
        );
      }
    });
    this.subs.push(sub);
  }

  /**
   * Fires once the srcdoc iframe finishes rendering. Reads the real content
   * height off the loaded document so the preview stage can size to however
   * many pages this CV actually has, instead of assuming exactly one.
   *
   * srcdoc loads even while its nb-tab is inactive (Nebular keeps inactive
   * tab content in the DOM under display:none rather than removing it), and
   * a display:none subtree lays out at zero height, so `load` firing on the
   * Letter tab (or during a refine turn started from it) would otherwise
   * measure 0 and permanently fall back to the single-page height, clipping
   * every page past the first with no further chance to correct it, since
   * activating the tab later only toggles a class, not another `load` event.
   * onTabChange re-measures from the already-loaded iframe once it does
   * become visible to cover exactly that case.
   */
  public onPreviewIframeLoad(event: Event): void {
    const doc = (event.target as HTMLIFrameElement).contentDocument;
    this.measurePreviewHeight(doc);
    // Every srcdoc reload (refresh, refine turn, tab swap) fires `load` again
    // on a NEW contentDocument, so the previous listener must be torn down
    // here -- otherwise each refresh accumulates one more click listener on
    // whatever document is current, none of which get cleaned up until
    // ngOnDestroy.
    if (this.previewClickDoc) {
      this.previewClickDoc.removeEventListener('click', this.previewClickHandler);
    }
    if (doc) {
      doc.addEventListener('click', this.previewClickHandler);
      this.previewClickDoc = doc;
    }
  }

  private measurePreviewHeight(doc: Document | null | undefined): void {
    // Hidden tab: Chrome reads 0, but Safari lays the document out at width 0
    // and reports a huge non-zero height (every word on its own line), which
    // would stick, since onTabChange only re-measures a null height. A zero
    // viewport width means "not laid out", whatever the height says.
    if (!doc || doc.documentElement.clientWidth === 0) return;
    const height = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0);
    if (height > 0) { this.previewContentHeight = Math.min(height, MAX_PREVIEW_CONTENT_HEIGHT_PX); }
  }

  /**
   * Fetch the templated letter for the letter tab. Called from refreshPreview()
   * (every load/refine/accept/poll site) and from the poll directly, but
   * fetches only when the STORED letter changed: CV-only refreshes (inclusion
   * toggles, CV refines, poll ticks) are frequent and must not re-render an
   * unchanged letter each time. A package without a letter clears the pane
   * and fetches nothing.
   *
   * Blind spot, handled by the callers that know: the fingerprint is the
   * letter content, but the render also reads the CV header (name, contact
   * rows) and the package language (date line). Redraft rewrites both without
   * touching letter_document, so onRegenerate() clears the fingerprint first.
   */
  public refreshLetterPreview(): void {
    if (!this.pkg) return;
    const doc = this.pkg.letter_document;
    if (!doc) {
      this.letterPreviewHtml = null;
      this.letterPreviewFingerprint = null;
      return;
    }
    const fingerprint = JSON.stringify(doc);
    if (fingerprint === this.letterPreviewFingerprint && this.letterPreviewHtml) return;
    this.letterPreviewFingerprint = fingerprint;
    const seq = ++this.letterPreviewSeq;
    this.letterPreviewLoading = true;
    this.letterPreviewFailed = false;
    const sub = this.tailor.previewLetter(this.pkg.id).subscribe({
      next: html => {
        if (seq !== this.letterPreviewSeq) return;
        // Same contract as previewHtml: consumed by an isolated <iframe> via
        // srcdoc, never injected into the main document.
        this.letterPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(html || '');
        this.letterPreviewContentHeight = null;
        this.letterPreviewLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        if (seq !== this.letterPreviewSeq) return;
        this.letterPreviewLoading = false;
        // Let the next refresh retry instead of being fingerprint-skipped.
        this.letterPreviewFingerprint = null;
        if (!this.letterPreviewHtml) { this.letterPreviewFailed = true; }
        console.error(err);
        this.toastr.warning(
          this.previewErrorMessage(err, 'cv.errors.letterPreviewFailed'),
          this.translate.instant('cv.errors.title') || 'Error');
      }
    });
    this.subs.push(sub);
  }

  /**
   * Toast text for a failed preview fetch. Both preview endpoints are fetched
   * with responseType 'text', so on a 503 err.error is the JSON envelope as a
   * STRING, not an object; parse it or the server message and correlation id
   * never reach the toast.
   */
  private previewErrorMessage(err: HttpErrorResponse, fallbackKey: string): string {
    let body: any = err.error;
    if (typeof body === 'string') {
      try { body = JSON.parse(body); } catch { body = null; }
    }
    const msg = body?.message || this.translate.instant(fallbackKey) || 'Preview could not be refreshed.';
    const ref = body?.correlation_id ? ` (ref: ${body.correlation_id})` : '';
    return msg + ref;
  }

  /**
   * Like the CV preview, the letter iframe renders at a fixed native width
   * and is only CSS-scaled to fit its container. Measure `.page`, not the
   * document viewport: scrollHeight includes the iframe's current height and
   * turns a short letter into a mostly blank, page-tall scroll region.
   *
   * Same hidden-tab trap as onPreviewIframeLoad: a cv+letter package opens on
   * the CV tab, so this srcdoc loads under display:none and measures 0. The
   * guard keeps the default until onTabChange re-measures once the letter tab
   * is visible.
   */
  public onLetterPreviewIframeLoad(event: Event): void {
    const iframe = event.target as HTMLIFrameElement;
    this.measureLetterPreviewHeight(iframe.contentDocument);
  }

  private measureLetterPreviewHeight(doc: Document | null | undefined): void {
    if (!doc || doc.documentElement.clientWidth === 0) return; // see measurePreviewHeight
    const page = doc.querySelector?.('.page') as HTMLElement | null | undefined;
    const pageHeight = page
      ? Math.max(page.scrollHeight, Math.ceil(page.getBoundingClientRect().height))
      : 0;
    const height = pageHeight || Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0);
    if (height > 0) { this.letterPreviewContentHeight = Math.min(Math.max(height, 320), MAX_PREVIEW_CONTENT_HEIGHT_PX); }
  }

  private previewClickDoc: Document | null = null;
  private previewClickHandler = (event: MouseEvent): void => {
    const target = (event.target as HTMLElement).closest('[data-edit-field]') as HTMLElement | null;
    if (!target || !this.pkg) { return; }
    const field = target.getAttribute('data-edit-field');
    const id = target.getAttribute('data-edit-id');
    this.selectFieldFromPreview(field, id ? Number(id) : null);
  };

  /** Maps a clicked preview element's data-edit-field/data-edit-id back to the matching EditSelection. */
  private selectFieldFromPreview(field: string | null, id: number | null): void {
    // The server only emits data-edit-field hooks while the flag is on, but
    // the click listener is installed on every preview load regardless.
    if (!this.pkg || !this.canManualEdit) { return; }
    switch (field) {
      case 'position': this.selectField({ kind: 'cv', field: 'position' }); return;
      case 'profile_text': this.selectField({ kind: 'cv', field: 'profile_text' }); return;
      case 'role': {
        const row = (this.pkg.experiences ?? []).find(e => e.role_id === id);
        if (row) { this.selectField({ kind: 'cv', field: 'role', experienceId: row.role_id, label: row.employer, current: row.role }); }
        return;
      }
      case 'description': {
        const row = (this.pkg.experiences ?? []).find(e => e.role_id === id);
        if (row) { this.selectField({ kind: 'cv', field: 'description', experienceId: row.role_id, label: row.employer, current: row.description }); }
        return;
      }
      case 'education_description': {
        const row = (this.pkg.education_inclusion ?? []).find(e => e.edu_id === id);
        if (row) { this.selectField({ kind: 'cv', field: 'education_description', educationId: row.edu_id, label: row.degree, current: row.description }); }
        return;
      }
      case 'skill_label': {
        const row = (this.pkg.section_inclusion?.skill ?? []).find(s => s.item_id === id);
        if (row) { this.selectField({ kind: 'cv', field: 'skill_label', skillId: row.item_id, label: '', current: row.name }); }
        return;
      }
      case 'soft_skill_label': {
        const row = (this.pkg.section_inclusion?.soft_skill ?? []).find(s => s.item_id === id);
        if (row) { this.selectField({ kind: 'cv', field: 'soft_skill_label', skillId: row.item_id, label: '', current: row.name }); }
        return;
      }
      case 'contact_email': this.selectField({ kind: 'cv', field: 'contact', contactField: 'email' }); return;
      case 'contact_phone': this.selectField({ kind: 'cv', field: 'contact', contactField: 'phone' }); return;
      case 'contact_linkedin_url': this.selectField({ kind: 'cv', field: 'contact', contactField: 'linkedin_url' }); return;
      case 'contact_address': this.selectField({ kind: 'cv', field: 'contact', contactField: 'address' }); return;
      case 'experience_period': {
        const row = (this.pkg.experiences ?? []).find(e => e.role_id === id);
        if (row) { this.selectField({ kind: 'cv', field: 'period', section: 'experience', id: row.role_id, label: row.title, start: row.start, end: row.end }); }
        return;
      }
      case 'education_period': {
        const row = (this.pkg.education_inclusion ?? []).find(e => e.edu_id === id);
        if (row) { this.selectField({ kind: 'cv', field: 'period', section: 'education', id: row.edu_id, label: row.degree || row.role, start: row.start, end: row.end }); }
        return;
      }
      case 'education_degree':
      case 'education_title':
      case 'education_institution': {
        const row = (this.pkg.education_inclusion ?? []).find(e => e.edu_id === id);
        if (!row) { return; }
        const edField = field === 'education_degree' ? 'degree' : field === 'education_title' ? 'role' : 'employer';
        const current = edField === 'degree' ? row.degree : edField === 'role' ? row.role : row.employer;
        this.selectField({ kind: 'cv', field: 'education_text', educationId: row.edu_id, edField, label: row.employer, current });
        return;
      }
    }
  }

  // Turn-state application has two shapes, deliberately NOT merged: a full
  // response (load/create/poll) replaces pkg/diff/coverage wholesale, so absent
  // diff/coverage CLEAR; a partial refine/inclusion turn patches only what it
  // carries (a letter refine returns just { package }) so absent diff/coverage
  // are PRESERVED, not cleared, and then refreshes the preview.
  private applyFullTurn(
    resp: {
      package: ApplicationPackage; diff?: DocDiff; coverage?: CoverageReport; rejected?: RejectedOp[];
      quota?: TailoringQuota | null; conversation?: ConversationTurn[];
      pending_proposal?: { cv: ProposalRef | null; letter: ProposalRef | null } | null;
    },
    opts?: { keepRejected?: boolean },
  ): void {
    this.pkg = resp.package;
    this.rebuildSectionInclusion();
    this.diff = resp.diff || {};
    this.coverage = resp.coverage || { matched: 0, total: 0, missing: [] };
    this.rejectedOps = opts?.keepRejected ? (resp.rejected || []) : [];
    // !== undefined (not a truthiness/nullish check): an envelope that omits
    // `quota` (e.g. create()'s, if it ever does) must leave the last known
    // value alone rather than blanking the chip; an explicit `null` from a
    // privileged caller must still clear it.
    if (resp.quota !== undefined) { this.quota = resp.quota; }
    this.conversation = resp.conversation ?? [];
    this.setPendingProposal(resp.pending_proposal ?? null);
    this.rebuildKindConversations();
  }

  // Split the flat conversation into the per-kind histories + round counts the
  // two kept-alive chat instances bind to. The initial async draft is ITSELF
  // recorded by TailorDraftRunner as an agent 'refine' turn, so it needs no
  // separate +1 here -- counting refine turns directly already includes it
  // (a package with no recorded turns yet correctly reads 0). Consult
  // (question) turns are excluded, matching the backend budget.
  private rebuildKindConversations(): void {
    const conv = this.conversation ?? [];
    this.cvConversation = conv.filter(t => t.kind === 'cv');
    this.letterConversation = conv.filter(t => t.kind === 'letter');
    this.cvTurnCount = this.cvConversation.filter(t => t.role === 'agent' && t.turn_type === 'refine').length;
    this.letterTurnCount = this.letterConversation.filter(t => t.role === 'agent' && t.turn_type === 'refine').length;
    // A still-pending proposal's refine turn is excluded from show()'s
    // conversation (it is rendered as the Accept/Reject card instead), but it
    // HAS spent a refine turn, so count it or the parent's number would fight
    // the chat's own local increment and the round counter would jump backward.
    if (this.pendingProposal?.cv) { this.cvTurnCount++; }
    if (this.pendingProposal?.letter) { this.letterTurnCount++; }
  }

  private applyPartialTurn(resp: TailorTurnResponse, opts?: { keepRejected?: boolean }): void {
    if (resp.package) { this.pkg = resp.package; this.rebuildSectionInclusion(); }
    if (resp.diff) this.diff = resp.diff;
    if (resp.coverage) this.coverage = resp.coverage;
    // Every endpoint behind this shape is a direct edit that supersedes the
    // open CV proposal server-side, and turnPayload() now says so. Without
    // this the review pane kept offering a proposal the server had already
    // discarded, and Accept on it answered 409.
    // NOT followed by rebuildKindConversations(): this response carries no
    // fresh conversation, so recounting here would drop the superseded turn's
    // +1 and hand back a refine round the user has already spent.
    if (resp.pending_proposal !== undefined) {
      this.setPendingProposal(resp.pending_proposal ?? null);
    }
    if (opts?.keepRejected) { this.rejectedOps = resp.rejected || []; }
    // refine()/redraft() carry a live quota so the "AI edits left" chip no
    // longer freezes at its page-load value. Same !== undefined defensiveness
    // as applyFullTurn.
    if (resp.quota !== undefined) { this.quota = resp.quota; }
    this.refreshPreview();
  }

  // --------------------------------------------------------------------
  // Create mode (intake absorbed): fetch CV defaults, then generate-on-arrival
  // when a vacancy is carried, else show the picker and create on pick.
  // --------------------------------------------------------------------

  private enterCreateMode(): void {
    // The router reuses this component instance across /tailor/:id and
    // /tailor/new, so ngOnDestroy does not fire between them. Stop any poll from
    // a prior generating package and drop its state so it cannot keep writing
    // this.pkg while we are on the create landing.
    this.pollSub?.unsubscribe();
    this.pkg = null;
    this.rebuildSectionInclusion();
    this.rejectedOps = [];
    // Reset landing state in case the component is reused for a 2nd intake.
    this.pickerVisible = false;
    this.createFailed = false;
    this.landingResults = [];
    this.landingError = false;
    this.landingPasteOpen = false;
    this.pastedText = '';
    this.landingPaste = false;
    this.landingQuery.setValue('', { emitEvent: false });
    // Clear carried client/template context so a reuse without ?client= does
    // not send a stale branded client_id from a previous run.
    this.cClientId = undefined;
    this.cTemplateType = 'built_in';
    this.cClientCvTemplateId = null;
    this.cSubmissionId = null;
    if (!this.landingWired) {
      this.landingWired = true;
      this.subs.push(this.landingQuery.valueChanges.pipe(debounceTime(250), distinctUntilChanged())
        .subscribe(q => void this.searchVacancies(q ?? '')));
    }

    const q = this.route.snapshot.queryParamMap;
    const subject = Number(q.get('subject'));
    if (!subject) { this.createFailed = true; return; }
    this.cSubject = subject;
    this.cVacancy = this.numOrNull(q.get('vacancy'));
    this.cSubmissionId = this.numOrNull(q.get('submission'));
    const client = q.get('client');
    const hasClient = client != null && client !== '' && client !== 'null';
    if (hasClient) {
      this.cClientId = Number(client);
    }
    this.cArtifacts = this.parseArtifacts(q);
    const carriedTemplate = q.get('template');
    const carriedLocale = q.get('locale');
    // Explicit query-param carry (e.g. re-selecting a template on a redraft)
    // wins; otherwise fall back to the client's configured default template
    // (App\Cv\CvTemplateSelection::resolveForClient(), surfaced via
    // curation_defaults) instead of hardcoding 'built_in'.
    const carriedTemplateType = q.get('template_type');
    const carriedClientCvTemplateId = this.numOrNull(q.get('client_cv_template_id'));

    const params: { client_id?: number; for?: string } = hasClient ? { client_id: this.cClientId } : { for: 'tailoring' };
    this.creating = true;
    const sub = this.cv.getDefaults(this.cSubject, params).subscribe({
      next: d => {
        this.templates = d.templates || [];
        this.cTemplateId = carriedTemplate || d.curation_defaults?.template_id || d.templates?.[0]?.id || 'editorial';
        this.cTemplateType = carriedTemplateType || d.curation_defaults?.template_type || 'built_in';
        this.cClientCvTemplateId = carriedClientCvTemplateId ?? d.curation_defaults?.client_cv_template_id ?? null;
        this.cLocale = carriedLocale || d.curation_defaults?.locale || 'nl';
        // A carried vacancy OR a carried submission (manual deal, §9) both
        // auto-create; only a bare visit lands on the picker.
        if (this.cVacancy || this.cSubmissionId) { this.autoCreate(); }
        else { this.creating = false; this.pickerVisible = true; }
      },
      error: () => { this.creating = false; this.createFailed = true; },
    });
    this.subs.push(sub);
  }

  public onPickedVacancy(v: MarketVacancy): void {
    this.cVacancy = v.id;
    this.landingPaste = false;
    this.pickerVisible = false;
    this.autoCreate();
  }

  /**
   * Start from a CRM deal (§9): a vacancy-backed deal reuses its vacancy as
   * context; a manual deal generates from the submission's Opportunity
   * (backend derives title/org/deadline). Either way the package auto-links.
   */
  public onPickedDeal(d: Submission): void {
    this.cSubmissionId = d.id;
    this.cVacancy = d.opportunity?.MarketVacancy_id ?? null;
    this.landingPaste = false;
    this.pickerVisible = false;
    this.autoCreate();
  }

  private async searchVacancies(q: string): Promise<void> {
    this.landingError = false;
    if (q.trim().length < 2) { this.landingResults = []; this.landingDeals = []; return; }
    this.landingSearching = true;
    try {
      const page = await this.vacancies.findMarketVacancies(1, { stuff: q.trim(), status: 1 }, []);
      if (!page || !Array.isArray(page.data)) { throw new Error('bad'); }
      this.landingResults = page.data.slice(0, 8);
    } catch (e) { console.error('landing search failed', e); this.landingResults = []; this.landingError = true; }
    finally { this.landingSearching = false; }
    // CRM deals of this consultant. Gated client-side: board() is
    // staffer/admin-only and APIService toasts every failed GET, so calling it
    // as a consultant would red-toast each debounced keystroke.
    if (!this.canSearchDeals) { this.landingDeals = []; return; }
    const deals = await this.submissions.board({
      search: q.trim(), consultant_user_id: this.cSubject ?? 0, per_page: 5,
    });
    this.landingDeals = deals?.data ?? [];
  }

  public submitPaste(): void { if (this.pastedText.trim()) { this.landingPaste = true; this.autoCreate(); } }

  private autoCreate(): void {
    if (!this.cSubject) { this.createFailed = true; return; }
    this.creating = true;
    this.createFailed = false;
    const body: CreatePackageRequest = {
      artifacts: this.cArtifacts,
      template_id: this.cTemplateId,
      locale: this.cLocale,
      template_type: this.cTemplateType,
      client_cv_template_id: this.cClientCvTemplateId,
      claim_strength: 'soft',
    };
    if (this.landingPaste) { body.pasted_vacancy_text = this.pastedText.trim(); }
    else { body.vacancy_id = this.cVacancy; }
    if (this.cClientId && this.cClientId > 0) { body.client_id = this.cClientId; }
    if (this.cSubmissionId) { body.submission_id = this.cSubmissionId; }
    const sub = this.tailor.createPackage(this.cSubject, body).subscribe({
      next: resp => {
        this.creating = false;
        this.mode = 'load';
        this.applyFullTurn(resp, { keepRejected: true });
        this.activeTab = this.cvTabActive ? 'cv' : 'letter';
        this.packageId = resp.package.id;
        // tailor/new -> tailor/:packageId is a route change (different path),
        // not a param change on the same route, so Angular recreates this
        // component and load() re-derives cArtifacts from the URL. Carry the
        // request here or that fresh instance loses it and cvTabActive's gate
        // silently reverts to its ['cv'] default.
        void this.router.navigate(['/cv-generator/tailor', resp.package.id], {
          replaceUrl: true,
          queryParams: { artifacts: this.cArtifacts.join(',') },
        });
        // create() now returns a `generating` package: poll until the draft
        // lands instead of blocking the request on the LLM. The preview is
        // refreshed by the poll once an artifact reaches draft.
        if (this.stillGenerating(resp.package)) { this.startPolling(); }
        else { this.refreshPreview(); }
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
        // A consultant's only route to their own package is this launcher, and
        // create() refuses one that is already submitted or finalized. Both
        // are their OWN work, so send them to it read-only (isLocked already
        // disables every mutating control) instead of to the dead-end
        // createFailed screen. Deliberately NOT extended to the 409 (a package
        // someone else started): withholding that one is design, and it
        // carries no package_id to navigate to. The route change re-enters the
        // param subscription, which loads the package; `creating` stays true
        // so the spinner carries across instead of flashing the landing card.
        const code: string | undefined = err.error?.error;
        const reopenId: number | undefined = err.error?.package_id;
        if (err.status === 422 && reopenId
            && (code === 'package_submitted' || code === 'package_finalized')) {
          void this.router.navigate(['/cv-generator/tailor', reopenId], {
            replaceUrl: true,
            queryParams: { artifacts: this.cArtifacts.join(',') },
          });
          return;
        }
        this.creating = false;
        this.createFailed = true;
        const msg = err.status === 429
          ? this.translate.instant('cv.errors.quotaReached')
          : (err.error?.message || this.translate.instant('cv.tailor.intake.errorCreate'));
        this.toastr.danger(msg, this.translate.instant('cv.errors.title') || 'Error');
      },
    });
    this.subs.push(sub);
  }

  public retryCreate(): void { this.createFailed = false; if (this.cVacancy || this.cSubmissionId) { this.autoCreate(); } else { this.pickerVisible = true; } }

  // --------------------------------------------------------------------
  // Async generation: poll show() until each requested artifact lands, then
  // refresh the preview. A failed draft surfaces a Retry that re-posts create.
  // --------------------------------------------------------------------

  private stillGenerating(p: ApplicationPackage): boolean {
    return p.cv_status === 'generating' || p.letter_status === 'generating';
  }

  private startPolling(): void {
    this.pollSub?.unsubscribe();
    // takeWhile(..., true) emits the first non-generating value too, so the
    // terminal draft/finalized/failed state is applied before the stream ends.
    // catchError -> EMPTY keeps a transient getPackage() error from killing the
    // whole stream (an error inside switchMap would otherwise complete it and
    // strand the spinner); the next interval tick simply retries.
    this.pollSub = interval(2500).pipe(
      switchMap(() => this.tailor.getPackage(this.packageId).pipe(catchError(() => EMPTY))),
      takeWhile(resp => this.stillGenerating(resp.package), true),
    ).subscribe({
      next: resp => {
        const prevCv = this.pkg?.cv_status;
        this.applyFullTurn(resp);
        // The letter can land before the CV (one draft job per artifact);
        // fingerprint-guarded, so this is a no-op on every other tick.
        this.refreshLetterPreview();
        // Refresh the preview the moment the CV draft lands (do not wait for a
        // still-generating letter), and again once everything has settled.
        const cvNowReady = resp.package.cv_status === 'draft' || resp.package.cv_status === 'finalized';
        const cvWasReady = prevCv === 'draft' || prevCv === 'finalized';
        if ((cvNowReady && !cvWasReady) || !this.stillGenerating(resp.package)) {
          this.refreshPreview();
        }
      },
      error: () => { /* transient poll error: the next tick retries */ },
    });
    // pollSub is owned by this field (torn down on re-entry + in ngOnDestroy);
    // it is deliberately NOT pushed into subs[] so retries do not grow that array.
  }

  /** Re-post create for the artifact(s) whose draft failed, then re-poll. */
  public retryGenerate(): void {
    if (!this.pkg) { return; }
    const body: CreatePackageRequest = {
      artifacts: this.failedArtifacts(),
      template_id: this.pkg.cv_template_id,
      locale: this.pkg.language,
      template_type: this.pkg.cv_template_type,
      claim_strength: (this.pkg.intake?.claim_strength as ClaimStrength) || 'soft',
    };
    // Carry the rest of the original intake so a retry does not silently null it.
    if (this.pkg.intake?.availability) { body.availability = this.pkg.intake.availability; }
    if (this.pkg.intake?.salutation) { body.salutation = this.pkg.intake.salutation; }
    if (this.pkg.client_cv_template_id != null) { body.client_cv_template_id = this.pkg.client_cv_template_id; }
    if (this.pkg.vacancy_id != null) { body.vacancy_id = this.pkg.vacancy_id; }
    else if (this.pkg.pasted_vacancy_text) { body.pasted_vacancy_text = this.pkg.pasted_vacancy_text; }
    if (this.pkg.client_id) { body.client_id = this.pkg.client_id; }

    const sub = this.tailor.createPackage(this.pkg.subject_user_id, body).subscribe({
      next: resp => {
        this.pkg = resp.package;
        this.rebuildSectionInclusion();
        this.packageId = resp.package.id;
        if (this.stillGenerating(resp.package)) { this.startPolling(); }
        else { this.refreshPreview(); }
      },
      error: (err: HttpErrorResponse) => {
        this.toastr.danger(
          err.error?.error || this.translate.instant('cv.tailor.workspace.retryFailed') || 'Retry failed',
          this.translate.instant('cv.errors.title') || 'Error');
      },
    });
    this.subs.push(sub);
  }

  private failedArtifacts(): ArtifactKind[] {
    const a: ArtifactKind[] = [];
    if (this.pkg?.cv_status === 'failed') { a.push('cv'); }
    if (this.pkg?.letter_status === 'failed') { a.push('letter'); }
    return a.length ? a : ['cv'];
  }

  private numOrNull(raw: string | null): number | null {
    if (raw == null || raw === '' || raw === 'null') return null;
    const n = Number(raw); return Number.isNaN(n) ? null : n;
  }

  private parseArtifacts(q: ParamMap): ArtifactKind[] {
    const a = (q.get('artifacts') || 'cv').split(',').filter(x => x === 'cv' || x === 'letter') as ArtifactKind[];
    return a.length ? a : ['cv'];
  }

  // --------------------------------------------------------------------
  // Experience inclusion cockpit: deterministic role actions (no LLM call).
  // The parent owns the HTTP call + state refresh; the panel emits intent.
  // --------------------------------------------------------------------

  public get experiences(): ExperienceInclusion[] {
    return this.pkg?.experiences ?? [];
  }

  public get education(): EducationInclusion[] {
    return this.pkg?.education_inclusion ?? [];
  }

  public sectionInclusion: { key: SectionKey; label: string; items: SectionInclusionItem[] }[] = [];

  private rebuildSectionInclusion(): void {
    const si = this.pkg?.section_inclusion ?? {};
    const defs: { key: SectionKey; labelKey: string }[] = [
      { key: 'skill',         labelKey: 'cv.tailor.inclusion.sectionSkills' },
      { key: 'soft_skill',    labelKey: 'cv.tailor.inclusion.sectionSoftSkills' },
      { key: 'certification', labelKey: 'cv.tailor.inclusion.sectionCertifications' },
      { key: 'language',      labelKey: 'cv.tailor.inclusion.sectionLanguages' },
    ];
    this.sectionInclusion = defs.map(d => ({
      key: d.key,
      label: this.translate.instant(d.labelKey),
      items: si[d.key] ?? [],
    }));
  }

  public onSectionSetAside(e: { section: SectionKey; itemId: number }): void {
    if (this.pkg) this.applyInclusion(this.tailor.setAsideItem(this.pkg.id, e.section, e.itemId));
  }

  public onSectionRestore(e: { section: SectionKey; itemId: number }): void {
    if (this.pkg) this.applyInclusion(this.tailor.restoreItem(this.pkg.id, e.section, e.itemId));
  }

  private applyInclusion(obs: Observable<TailorTurnResponse>): void {
    this.inclusionBusy = true;
    const sub = obs.subscribe({
      next: resp => {
        this.inclusionBusy = false;
        this.applyPartialTurn(resp);
      },
      error: (err: HttpErrorResponse) => {
        this.inclusionBusy = false;
        console.error(err);
        const title = this.translate.instant('cv.errors.title') || 'Error';
        this.toastr.warning(this.writeErrorMessage(err, 'cv.errors.title'), title);
        // These endpoints now guard their write on the document they read for
        // their own pre-check, so a concurrent edit answers 409 stale_document
        // instead of applying to a document this client never saw. Nothing on
        // screen matches the server any more, so reload rather than leave the
        // user to guess -- the same thing saveEdit() does.
        if (this.isStaleDocument(err)) { this.load(); }
      },
    });
    this.subs.push(sub);
  }

  public onRestore(roleId: number): void { if (this.pkg) this.applyInclusion(this.tailor.restoreRole(this.pkg.id, roleId)); }
  public onSetAside(roleId: number): void { if (this.pkg) this.applyInclusion(this.tailor.setAsideRole(this.pkg.id, roleId)); }

  public reorderExperiences(order: number[]): void {
    if (!this.pkg) { return; }
    this.editBusy = true;
    const sub = this.tailor.edit(this.pkg.id, {
      kind: 'cv', base_fingerprint: this.pkg.cv_fingerprint ?? null,
      ops: [{ op: 'reorder_experiences', order }],
    }).subscribe({
      next: resp => { this.editBusy = false; this.onTurn(resp); },
      error: (err: HttpErrorResponse) => { this.editBusy = false; this.reportEditFailure(err, { reload: true }); },
    });
    this.subs.push(sub);
  }

  public reorderSection(section: SectionKey, order: number[]): void {
    if (!this.pkg) { return; }
    this.editBusy = true;
    const sub = this.tailor.edit(this.pkg.id, {
      kind: 'cv', base_fingerprint: this.pkg.cv_fingerprint ?? null,
      ops: [{ op: 'reorder_items', section, order }],
    }).subscribe({
      next: resp => { this.editBusy = false; this.onTurn(resp); },
      error: (err: HttpErrorResponse) => { this.editBusy = false; this.reportEditFailure(err, { reload: true }); },
    });
    this.subs.push(sub);
  }

  /** Reorders letter paragraphs by dragging; posts the full array, same as a text edit. */
  public onLetterDrop(event: CdkDragDrop<{ key: number; text: string }[]>): void {
    if (!this.pkg) { return; }
    const rows = [...this.letterParagraphRows];
    moveItemInArray(rows, event.previousIndex, event.currentIndex);
    const paragraphs = rows.map(r => r.text);

    this.editBusy = true;
    const sub = this.tailor.edit(this.pkg.id, {
      kind: 'letter', base_fingerprint: this.pkg.letter_fingerprint ?? null, paragraphs,
    }).subscribe({
      next: resp => { this.editBusy = false; this.onTurn(resp); },
      error: (err: HttpErrorResponse) => { this.editBusy = false; this.reportEditFailure(err, { reload: true }); },
    });
    this.subs.push(sub);
  }

  public onEducationSetAside(eduId: number): void { if (this.pkg) this.applyInclusion(this.tailor.setAsideEducation(this.pkg.id, eduId)); }
  public onEducationRestore(eduId: number): void { if (this.pkg) this.applyInclusion(this.tailor.restoreEducation(this.pkg.id, eduId)); }
  // Show/hide toggle AND inline description text: both pre-existing,
  // always-on controls, so they stay on the dedicated
  // .../education/{id}/description endpoint, which is not flag-gated and
  // accepts `text` in either flag state. `text` must be carried through or
  // the flag-off panel's Save posts a bare toggle and silently discards what
  // the user typed. What IS behind the flag is only the ROUTE: with manual
  // editing on the panel emits editDescription instead, and the text goes
  // through the shared field editor and tailor.edit().
  public onEducationDescription(e: { eduId: number; show: boolean; text?: string }): void {
    if (!this.pkg) return;
    const body: { show: boolean; text?: string } = { show: e.show };
    if (e.text !== undefined) { body.text = e.text; }
    this.applyInclusion(this.tailor.setEducationDescription(this.pkg.id, e.eduId, body));
  }

  // --------------------------------------------------------------------
  // Settings drawer: re-draft the package against changed template/locale/
  // claim/availability/salutation. The intake (settings mode) emits the body.
  // --------------------------------------------------------------------

  public onRegenerate(s: {
    template_id: string;
    locale: string;
    template_type?: string;
    client_cv_template_id?: number | null;
    claim_strength: ClaimStrength;
    availability?: string;
    salutation?: string;
  }): void {
    if (!this.pkg) return;
    this.inclusionBusy = true;
    const sub = this.tailor.redraft(this.pkg.id, {
      template_id: s.template_id,
      locale: s.locale,
      template_type: s.template_type || 'built_in',
      client_cv_template_id: s.client_cv_template_id ?? null,
      claim_strength: s.claim_strength,
      availability: s.availability, salutation: s.salutation,
    }).subscribe({
      next: resp => {
        this.inclusionBusy = false;
        this.drawerOpen = false;
        // Redraft rewrites the CV header and the language the letterhead
        // reads, without touching letter_document; force the letter re-fetch.
        this.letterPreviewFingerprint = null;
        this.applyPartialTurn(resp, { keepRejected: true });
      },
      error: (err: HttpErrorResponse) => {
        this.inclusionBusy = false;
        const msg = err.status === 429
          ? this.translate.instant('cv.errors.quotaReached')
          : (err.error?.message || this.translate.instant('cv.tailor.intake.errorCreate'));
        this.toastr.danger(msg, this.translate.instant('cv.errors.title') || 'Error');
      },
    });
    this.subs.push(sub);
  }

  /**
   * Settings-mode template pick: swap the template WITHOUT re-running the agent
   * (#723). The preview loading state is shown immediately so the half-rendered
   * frame during the swap is never visible (#722).
   */
  public onTemplateChange(s: { template_id: string; template_type: string; client_cv_template_id: number | null }): void {
    if (!this.pkg) return;
    this.previewLoading = true;
    this.previewFailed = false;
    const sub = this.tailor.setTemplate(this.pkg.id, {
      template_id: s.template_id,
      template_type: s.template_type,
      client_cv_template_id: s.client_cv_template_id,
    }).subscribe({
      next: resp => {
        this.applyPartialTurn(resp);
      },
      error: (err: HttpErrorResponse) => {
        this.previewLoading = false;
        if (!this.previewHtml) { this.previewFailed = true; }
        const msg = err.error?.message || this.translate.instant('cv.tailor.intake.errorCreate');
        this.toastr.danger(msg, this.translate.instant('cv.errors.title') || 'Error');
      },
    });
    this.subs.push(sub);
  }

  // --------------------------------------------------------------------
  // Chat turn applied: the stored doc changed, so re-pull diff + preview.
  // --------------------------------------------------------------------

  public onTurn(resp: TailorTurnResponse): void {
    if (resp.package) { this.pkg = resp.package; this.rebuildSectionInclusion(); }
    // This is the chat refine turn's own handler, NOT applyPartialTurn --
    // it never routed through there, so the live quota needs the same
    // defensive update here or the chip stays frozen across every chat turn
    // (the failure scenario the finding was actually filed against).
    if (resp.quota !== undefined) { this.quota = resp.quota; }
    // Accept/reject deliver the full show() envelope (a superset of the turn
    // response). Forward its conversation + pending proposal so each per-kind
    // chat instance's cache re-seeds and the just-resolved proposal clears (F14);
    // for a reject this also restores the live document's coverage the discarded
    // proposal had replaced on screen (F6). A plain refine/consult response
    // carries neither key, so this branch is skipped for those.
    const env = resp as Partial<PackageShowResponse>;
    if (env.conversation !== undefined || env.pending_proposal !== undefined) {
      this.conversation = env.conversation ?? [];
      this.setPendingProposal(env.pending_proposal ?? null);
      if (env.coverage !== undefined) { this.coverage = env.coverage; }
      this.rebuildKindConversations();
    }
    // A fresh refine returns only its OWN proposal, never the whole pending
    // map, so mirror it in: the review panes read pendingProposal, and without
    // this they stay empty until the next show(). Which slot it fills is read
    // off the payload rather than the active tab, for the same reason the
    // diff/coverage branch below does: the user may have switched tabs while
    // the turn was in flight. The other slot is preserved, since the two
    // artifacts hold independent proposals.
    if (resp.proposal) {
      const isLetter = resp.proposal.letter !== undefined;
      this.setPendingProposal({
        cv:     isLetter ? (this.pendingProposal?.cv ?? null) : resp.proposal,
        letter: isLetter ? resp.proposal : (this.pendingProposal?.letter ?? null),
      });
    }
    // Classify by the RESPONSE shape, not the (mutable) active tab: a CV refine
    // carries diff/coverage, a letter refine returns only { package }. Reading
    // activeTab here misfires if the user switched tabs while the turn was in
    // flight (Codex review).
    if (resp.diff !== undefined || resp.coverage !== undefined) {
      if (resp.diff !== undefined) { this.diff = resp.diff || {}; }
      if (resp.coverage !== undefined) { this.coverage = resp.coverage || { matched: 0, total: 0, missing: [] }; }
      this.rejectedOps = resp.rejected || [];
      this.refreshPreview();
    }
  }

  // --------------------------------------------------------------------
  // Finalize
  // --------------------------------------------------------------------

  public finalize(kind: ArtifactKind): void {
    if (!this.pkg || this.finalizing) return;
    this.finalizing = true;

    const sub = this.tailor.finalize(this.pkg.id, kind).subscribe({
      next: () => {
        this.finalizing = false;
        this.cvView = 'preview';
        this.toastr.success(
          this.translate.instant('cv.tailor.workspace.finalizeSuccess') || 'Documents generated.',
          this.translate.instant('cv.tailor.workspace.statusFinal') || 'Final'
        );
        // Statuses + pdf keys changed server-side; re-pull canonical state.
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.finalizing = false;
        console.error(err);
        const msg = err.error?.message
          || this.translate.instant('cv.tailor.workspace.finalizeError')
          || 'Genereren mislukt. Probeer het opnieuw.';
        const ref = err.error?.correlation_id ? ` (ref: ${err.error.correlation_id})` : '';
        this.toastr.danger(
          msg + ref,
          this.translate.instant('cv.errors.title') || 'Fout'
        );
      }
    });
    this.subs.push(sub);
  }

  public downloadUrl(kind: 'cv' | 'letter'): string {
    return this.pkg ? this.tailor.downloadUrl(this.pkg.id, kind) : '';
  }

  /**
   * Promote a finalized artifact into this client's house-style exemplars so
   * the AI can learn from it. Only callable once the artifact is finalized;
   * the template gates the button on cvFinalized / letterFinalized.
   */
  public promoteExemplar(kind: ArtifactKind): void {
    if (!this.pkg || this.promoting) { return; }
    const isFinal = kind === 'cv' ? this.cvFinalized : this.letterFinalized;
    if (!isFinal) { return; }

    this.promoting = true;
    const sub = this.houseExemplars.promote(this.pkg.id, kind).subscribe({
      next: () => {
        this.promoting = false;
        this.toastr.success(
          this.translate.instant('cv.tailor.workspace.promoteSuccess')
          || 'Sent to house examples. Review the distilled style guide there.',
          this.translate.instant('cv.tailor.workspace.promoteTitle') || 'House examples'
        );
      },
      error: (err: HttpErrorResponse) => {
        this.promoting = false;
        console.error('cv-tailor: promote exemplar failed', err);
        this.toastr.danger(
          err.error?.message
          || this.translate.instant('cv.tailor.workspace.promoteError')
          || 'Could not send this to house examples.',
          this.translate.instant('cv.errors.title') || 'Error'
        );
      }
    });
    this.subs.push(sub);
  }

  // --------------------------------------------------------------------
  // Derived state
  // --------------------------------------------------------------------

  @ViewChild('cvOverflow', { read: NbPopoverDirective }) cvOverflow: NbPopoverDirective;
  @ViewChild('letterOverflow', { read: NbPopoverDirective }) letterOverflow: NbPopoverDirective;
  @ViewChild('previewIframe') previewIframeRef?: ElementRef<HTMLIFrameElement>;
  @ViewChild('letterPreviewIframe') letterPreviewIframeRef?: ElementRef<HTMLIFrameElement>;

  /** Close the artifact's overflow popover after an item is chosen (template refs
   * don't cross the <ng-template> boundary — same constraint as PR #707). */
  public closeOverflow(kind: 'cv' | 'letter'): void {
    (kind === 'cv' ? this.cvOverflow : this.letterOverflow)?.hide();
  }

  public get cvFinalized(): boolean { return this.pkg?.cv_status === 'finalized'; }
  public get letterFinalized(): boolean { return this.pkg?.letter_status === 'finalized'; }
  // hasCv/hasLetter mean "has a usable draft" (draft or finalized). With async
  // generation, generating/failed are NOT usable, so they are excluded here and
  // surfaced through the dedicated getters below.
  public get hasCv(): boolean { const s = this.pkg?.cv_status; return s === 'draft' || s === 'finalized'; }
  public get hasLetter(): boolean { const s = this.pkg?.letter_status; return s === 'draft' || s === 'finalized'; }
  public get cvGenerating(): boolean { return this.pkg?.cv_status === 'generating'; }
  public get letterGenerating(): boolean { return this.pkg?.letter_status === 'generating'; }
  public get cvFailed(): boolean { return this.pkg?.cv_status === 'failed'; }
  public get letterFailed(): boolean { return this.pkg?.letter_status === 'failed'; }
  public get anyGenerating(): boolean { return this.cvGenerating || this.letterGenerating; }
  /** The artifact tab is shown (enabled) for any non-none state. */
  public get cvHasAny(): boolean { return this.hasCv || this.cvGenerating || this.cvFailed; }
  public get letterHasAny(): boolean { return this.hasLetter || this.letterGenerating || this.letterFailed; }
  // Which tab opens active: prefer a usable/working CV, but a CV that only
  // FAILED must not steal focus from a letter that already has a draft.
  // Gated on cArtifacts: a submission's package can carry a CV from an
  // earlier, separate generation, so "this package has a CV" is not proof the
  // user wants the CV tab -- only true when THIS request actually asked for
  // one. cArtifacts is re-derived from the `artifacts` URL param on every
  // load() (not just the create flow), and that param is carried through
  // both post-create navigations, so a Cover-letter-only pick keeps landing
  // on the letter tab across the tailor/new -> tailor/:packageId route swap,
  // a refresh, or a bookmarked link. Only a request with no artifacts param
  // at all (e.g. the CV list's row link, which carries no intent) falls back
  // to the ['cv'] default.
  public get cvTabActive(): boolean {
    if (!this.cArtifacts.includes('cv')) { return false; }
    return this.hasCv || this.cvGenerating || (this.cvFailed && !this.letterHasAny);
  }
  public get letterTabActive(): boolean { return !this.cvTabActive && this.letterHasAny; }
  // The refine chat only makes sense once the ACTIVE artifact has a usable draft;
  // while it is generating/failed the chat would falsely claim a version exists.
  public get activeArtifactReady(): boolean {
    return this.activeTab === 'cv' ? this.hasCv : this.hasLetter;
  }
  // Per-kind readiness gates the two kept-alive chat instances independently.
  // Readiness only flips false->true in normal use (a draft never un-drafts on
  // a tab switch), so an @if here mounts each instance once its artifact exists
  // without destroying it on a later tab switch — in-session turns survive.
  public get cvArtifactReady(): boolean { return this.hasCv; }
  public get letterArtifactReady(): boolean { return this.hasLetter; }

  /** Letter body paragraphs in reading order; the API already dropped blanks. */
  public get letterParagraphs(): string[] {
    return this.pkg?.letter_document?.paragraphs ?? [];
  }

  // Paragraphs have no server-side id, so this wraps each in its array index
  // as a stable `track` key. Recomputed fresh from `pkg` on every access and
  // used only for template `track`/drag identity within a single render --
  // safe because the array is always fully replaced on save (applyLetter),
  // never patched in place, so stale keys never persist across a save.
  public get letterParagraphRows(): { key: number; text: string }[] {
    return this.letterParagraphs.map((text, i) => ({ key: i, text }));
  }

  public get proposedLetterParagraphs(): string[] {
    return this.proposedLetter?.paragraphs ?? [];
  }

  public onTabChange(tab: { tabId?: string }): void {
    this.activeTab = tab?.tabId === 'letter' ? 'letter' : 'cv';
    // The preview iframe may have loaded (and measured 0) while this tab was
    // still display:none, see onPreviewIframeLoad's docblock. Re-measure now
    // that it's visible; rAF waits for the tab's content-active class to
    // actually paint before reading layout, since that class flip lands via
    // change detection just after this handler runs, not synchronously in it.
    if (this.activeTab === 'cv' && this.previewContentHeight === null) {
      requestAnimationFrame(() => this.measurePreviewHeight(this.previewIframeRef?.nativeElement.contentDocument));
    }
    if (this.activeTab === 'letter' && this.letterPreviewContentHeight === null) {
      requestAnimationFrame(() => this.measureLetterPreviewHeight(this.letterPreviewIframeRef?.nativeElement.contentDocument));
    }
  }

  // Per-artifact status badges on the tab strip (none = no badge).
  public get cvBadgeText(): string {
    if (this.cvFinalized) return this.translate.instant('cv.tailor.workspace.statusFinal');
    if (this.cvGenerating) return this.translate.instant('cv.tailor.workspace.statusGenerating');
    if (this.cvFailed) return this.translate.instant('cv.tailor.workspace.statusFailed');
    if (this.hasCv) return this.translate.instant('cv.tailor.workspace.statusDraft');
    return '';
  }
  public get cvBadgeStatus(): NbComponentStatus {
    if (this.cvFinalized) return 'success';
    if (this.cvGenerating) return 'info';
    if (this.cvFailed) return 'danger';
    return 'warning';
  }
  public get letterBadgeText(): string {
    if (this.letterFinalized) return this.translate.instant('cv.tailor.workspace.statusFinal');
    if (this.letterGenerating) return this.translate.instant('cv.tailor.workspace.statusGenerating');
    if (this.letterFailed) return this.translate.instant('cv.tailor.workspace.statusFailed');
    if (this.hasLetter) return this.translate.instant('cv.tailor.workspace.statusDraft');
    return '';
  }
  public get letterBadgeStatus(): NbComponentStatus {
    if (this.letterFinalized) return 'success';
    if (this.letterGenerating) return 'info';
    if (this.letterFailed) return 'danger';
    return 'warning';
  }

  public get coveragePct(): number {
    if (!this.coverage || this.coverage.total <= 0) return 0;
    return Math.round((this.coverage.matched / this.coverage.total) * 100);
  }

  public get coverageStatus(): string {
    if (!this.coverage || this.coverage.total <= 0) return 'basic';
    return this.coverage.matched >= this.coverage.total ? 'success' : 'basic';
  }

  public get coverageRows(): CoverageRequirement[] {
    return this.coverage.requirements ?? [];
  }

  public get keptRequiredRows(): RejectedOp[] {
    return this.rejectedOps.filter(r => r.reason === 'kept_required');
  }

  public coverageSkillLabels(row: CoverageRequirement): string {
    return row.skill_labels?.length
      ? row.skill_labels.join(', ')
      : this.translate.instant('cv.tailor.workspace.coverageMatched');
  }

  public rejectedLabel(row: RejectedOp): string {
    return row.label || (row.id !== undefined ? `#${row.id}` : row.op);
  }

  // --------------------------------------------------------------------
  // Diff readability helpers
  // --------------------------------------------------------------------

  public get hasChanges(): boolean {
    const d = this.reviewDiff;
    if (!d) return false;
    const hdr = !!(d.header && (d.header.position || d.header.profile_text));
    const exp = !!(d.experience && (
      (d.experience.changed && Object.keys(d.experience.changed).length > 0) ||
      (d.experience.added && d.experience.added.length > 0) ||
      (d.experience.removed && d.experience.removed.length > 0) ||
      d.experience.reordered
    ));
    const sk = this.groupChanged(d.skills);
    const ss = this.groupChanged(d.soft_skills);
    // DocDiff also emits education/certifications/languages, and ops exist that
    // touch only those (reword_education_description, drop on a certification).
    // Against the cumulative diff the omission never showed, because a drafted
    // CV essentially always carries a header or skill change too. Against one
    // turn's diff it read as "No changes yet." on a real proposal.
    const edu = !!(d.education && (
      (d.education.changed && Object.keys(d.education.changed).length > 0) || this.groupChanged(d.education)
    ));
    const cert = this.groupChanged(d.certifications);
    const lang = this.groupChanged(d.languages);
    return hdr || exp || sk || ss || edu || cert || lang;
  }

  public get changedEducationCount(): number {
    return this.reviewDiff.education?.changed ? Object.keys(this.reviewDiff.education.changed).length : 0;
  }

  public groupChanged(g?: { removed?: number[]; added?: number[]; reordered?: boolean }): boolean {
    if (!g) return false;
    return (g.added?.length ?? 0) > 0 || (g.removed?.length ?? 0) > 0 || !!g.reordered;
  }

  public removedRows(kind: 'skill' | 'soft_skill'): Array<{ id: number; label: string; reason: string }> {
    const group = kind === 'skill' ? this.reviewDiff.skills : this.reviewDiff.soft_skills;
    const ids = group?.removed ?? [];
    const labels = group?.removed_labels ?? [];
    const reasons = this.pkg?.set_aside_reasons ?? {};
    // A bulk reselection (set_sidebar_skills/set_soft_skills) drops skills with
    // no per-skill reason from the model; EditOpNormalizer/SetAsideReasons
    // leave that case with no stored reason rather than fabricate one, so an
    // empty value here means "none given", not "failed to load".
    return ids.map((id, idx) => ({
      id,
      label: labels[idx] || `#${id}`,
      reason: reasons[`${kind}:${id}`] || '',
    }));
  }

  public get changedExperienceCount(): number {
    return this.reviewDiff.experience?.changed ? Object.keys(this.reviewDiff.experience.changed).length : 0;
  }

  public text(v: unknown): string {
    if (v === null || v === undefined) return '';
    return String(v);
  }
}
